import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useNotifications } from "../contexts/NotificationContext";
import { Loader2, AlertCircle } from "lucide-react";
import { getSupabaseClient } from "../config/supabase";
import { Session, AuthError } from '@supabase/supabase-js';

export default function VerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("Verifying your login...");

  const { showNotification } = useNotifications();

  useEffect(() => {
    let isSubscribed = true;

    // Extract projectId from URL if present
    const urlParams = new URLSearchParams(location.search);
    const projectId = urlParams.get('projectId');
    
    // Extract access_token from URL hash if present
    const hash = location.hash;

    // Check for error parameters in the hash
    if (hash && hash.includes('error=')) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const error = hashParams.get('error');
      const errorDescription = hashParams.get('error_description');
      const errorCode = hashParams.get('error_code');
      
      if (error) {
        console.error('VerifyPage: Auth error in URL hash:', { error, errorCode, errorDescription });
        const errorMessage = errorDescription 
          ? decodeURIComponent(errorDescription.replace(/\+/g, ' ')) 
          : 'Authentication failed';
        
        setError(errorMessage);
        setStatus('Verification failed');
        
        // Show notification instead of just redirecting
        showNotification('error', errorMessage, 8000);
        
        setTimeout(() => {
          if (isSubscribed) {
            navigate('/login', {
              replace: true,
              state: { error: errorMessage }
            });
          }
        }, 5000);
        
        return; // Stop further processing
      }
    }

    // Try to extract access_token from URL hash
    const extractTokenFromHash = () => {
      if (hash && hash.includes('access_token=')) {
        const hashParams = new URLSearchParams(hash.substring(1));
        return hashParams.get('access_token');
      }
      return null;
    };
    
    const accessToken = extractTokenFromHash();
    if (accessToken) {
      
      // Manually set the session with the token from the URL
      getSupabaseClient().auth.setSession({ access_token: accessToken, refresh_token: '' })
        .then(({ error }) => {
          if (error) {
            console.error('VerifyPage: Error setting session:', error);
            setError(error.message);
            setStatus('Authentication failed');
            
            // Show notification for the error
            showNotification('error', error.message, 8000);
            
            setTimeout(() => {
              if (isSubscribed) {
                navigate('/login', {
                  replace: true,
                  state: { error: error.message }
                });
              }
            }, 5000);
          } else {
            showNotification('success', 'Successfully authenticated!', 3000);
          }
        });
    }

    // Handle auth state changes
    const { data: { subscription } } = getSupabaseClient().auth.onAuthStateChange(async (event: string, session: Session | null) => {

      if (!isSubscribed) return;

      if (event === 'SIGNED_IN' && session?.access_token) {
        try {
          await verifyToken(session.access_token);
          
          // If projectId is present, redirect to the project page
          if (projectId) {
            navigate(`/projects/${projectId}`, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Verification failed";
          setError(errorMessage);
          setStatus('Verification failed');

          // Show notification for the error
          showNotification('error', errorMessage, 8000);
          
          // For expired tokens, redirect after 5 seconds
          // For other errors, redirect after 8 seconds
          const isExpired = errorMessage.includes('expired');
          setTimeout(() => {
            if (isSubscribed) {
              navigate('/login', {
                replace: true,
                state: { error: errorMessage }
              });
            }
          }, isExpired ? 5000 : 8000);
        }
      } else if (event === 'SIGNED_OUT') {
        setStatus('Session ended');
        if (isSubscribed) {
          navigate('/login', { replace: true });
        }
      }
    });

    // Check for existing session
    getSupabaseClient().auth.getSession().then(({ data: { session }, error: sessionError }: { data: { session: Session | null }, error: AuthError | null }) => {
      if (!isSubscribed) return;

      if (sessionError) {
        console.error('VerifyPage: Session check failed:', sessionError);
        setError(sessionError.message);
        setStatus('Session check failed');
        navigate('/login', {
          replace: true,
          state: { error: sessionError.message }
        });
      } else if (!session) {
        // Check for error in location state
        const locationState = location.state as { error?: string } | null;
        if (locationState?.error) {
          setError(locationState.error);
          setStatus('Verification failed');
          showNotification('error', locationState.error, 8000);
        } else {
          setStatus('No active session');
          showNotification('info', 'No active session found. Please log in.', 5000);
        }
        
        // Delay redirect to allow notification to be seen
        setTimeout(() => {
          if (isSubscribed) {
            navigate('/login', { replace: true });
          }
        }, 3000);
      }
    });

    return () => {
      isSubscribed = false;
      subscription.unsubscribe();
    };
  }, [navigate, location, verifyToken]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10 text-center">
          {error ? (
            <>
              <AlertCircle className="mx-auto h-8 w-8 text-red-600" />
              <h2 className="mt-4 text-lg font-medium text-gray-900">
                Verification Failed
              </h2>
              <p className="mt-2 text-sm text-red-600">
                {error}
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Redirecting you back to login...
              </p>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto h-8 w-8 text-blue-600 animate-spin" />
              <h2 className="mt-4 text-lg font-medium text-gray-900">
                {status}
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                Please wait while we authenticate your session.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
